import { LinkWithSlug } from "@/router";
import { EllipsisVerticalIcon, ListXIcon } from "lucide-react";
import React, { Fragment, ReactNode, useMemo, useState } from "react";

import {
  Notification,
  useDismissAllNotificationsMutation,
} from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import { List } from "@/components/elements/List";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  SlideOver,
  SlideOverBody,
  SlideOverCloseButton,
  SlideOverHeader,
} from "@/components/elements/SlideOver";
import { SuccessIcon } from "@/components/icons/graphic";
import { AlertsIcon } from "@/components/icons/domain-icons";
import {
  NotificationDetailModalLazy,
  useNotificationAction,
  useNotifications,
  useRemoveNotification,
} from "@/features/notifications";
import { IfRole, usePermission } from "@/features/roleAccess";
import cn from "@/helpers/classNames";
import { useDisclosure } from "@/hooks/useDisclosure";
import { useToast } from "@/providers/ToastProvider";
import { Menu } from "@headlessui/react";

import TableMenu, { MenuButton } from "../TableMenu";
import { NotificationItem } from "./NotificationItem";
import { useNotificationItem } from "./useNotificationItem";

const SidebarNotificationsContext = React.createContext({
  notificationsCount: 0,
  toggleSidebar: () => {},
  hideSidebar: () => {},
});

export const useSidebarNotifications = () => {
  return React.useContext(SidebarNotificationsContext);
};

type SidebarNotificationsProps = {
  children?:
    | React.ReactNode
    | ((
        props: React.ContextType<typeof SidebarNotificationsContext>,
      ) => React.ReactNode);
};

const SidebarNotifications = (props: SidebarNotificationsProps) => {
  const [showNotifications, setShowNotifications] = useState(false);

  const { notifications, loading, refetch } = useNotifications();

  const [dismissAllNotifications, { loading: isDismissingAllNotifications }] =
    useDismissAllNotificationsMutation({
      onCompleted: () => {
        void refetch();
      },
    });

  const hasDismissableNotifications = notifications.some(
    (notification) => notification.isDismissable,
  );

  const ctx = useMemo(
    () => ({
      notificationsCount: notifications.length,
      toggleSidebar: () => {
        if (showNotifications === false) {
          // If we're opening the sidebar, we want to refetch to ensure we have the latest notifications
          void refetch();
        }
        setShowNotifications((value) => !value);
      },
      hideSidebar: () => {
        setShowNotifications(() => false);
      },
    }),
    [notifications, showNotifications, refetch],
  );

  return (
    <>
      <SidebarNotificationsContext.Provider value={ctx}>
        {typeof props.children === "function"
          ? props.children(ctx)
          : props.children}

        <SlideOver
          show={showNotifications}
          bgOverlay
          onClose={() => setShowNotifications(false)}
        >
          <SlideOverCloseButton />
          <SlideOverHeader className="">
            <div className="flex items-center space-x-2">
              <AlertsIcon className="size-5" />
              <span>Alerts</span>
            </div>
            <div className="flex items-center justify-end gap-2">
              {notifications.length > 0 && (
                <IfRole action="resolveNotification">
                  <Button
                    onClick={() => void dismissAllNotifications()}
                    size="sm"
                    variant="link"
                    isDisabled={!hasDismissableNotifications}
                    isLoading={isDismissingAllNotifications}
                    loadingText="Dismiss all"
                    icon={<ListXIcon />}
                  >
                    Dismiss all
                  </Button>
                </IfRole>
              )}
            </div>
          </SlideOverHeader>
          <SlideOverBody className="px-0">
            {loading && (
              <div className="flex w-full items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
            {notifications.length === 0 && !loading ? (
              <NotificationsEmptyState />
            ) : (
              <List as="div" role="list">
                {notifications.map((notification) => (
                  <NotificationRenderer
                    notification={notification}
                    key={notification.id}
                  />
                ))}
              </List>
            )}
          </SlideOverBody>
        </SlideOver>
      </SidebarNotificationsContext.Provider>
    </>
  );
};

export default SidebarNotifications;

function NotificationsEmptyState(_props: React.ComponentProps<"div">) {
  return (
    <div className="my-4 flex flex-col items-center justify-center gap-4 py-10">
      <SuccessIcon className="h-20 text-white" />
      <div className="">No alerts for account.</div>
    </div>
  );
}

function NotificationRenderer(props: { notification: Notification }) {
  const itemProps = useNotificationItem(props.notification);
  const disclosure = useDisclosure();

  const canResolve = usePermission("resolveNotification");

  const toast = useToast();
  const { getActionProps } = useNotificationAction({
    onCompleted: (message) => toast("Success", message, "success"),
    onError: (message) => toast("Error", message, "error"),
  });
  const actionProps = getActionProps(props.notification);

  const { hideSidebar } = useSidebarNotifications();

  const [removeNotification] = useRemoveNotification({
    onCompleted: () => {
      const message = props.notification.isLock ? "Sync will be retried" : "";
      toast("Alert removed", message, "success");
    },
    onError: () => {
      const message = props.notification.isLock
        ? "An error occurred while queueing the sync for retry"
        : "";
      toast("Alert not removed", message, "error");
    },
  });

  const items: ReactNode[] = [];
  actionProps.forEach((action) => {
    const clickHandler = (e: React.MouseEvent) => {
      e.stopPropagation();
      action.handler?.(e);
      if (action.to || action.search || action.hash) {
        hideSidebar();
      }
    };

    const getClassName = (active: boolean) => {
      return cn("group flex w-full items-center px-4 py-2 text-sm", {
        "bg-gray-100 dark:bg-gray-700": active,
        "dark:text-white": !active,
      });
    };

    if (action.to || action.search || action.hash) {
      const node = (
        <Menu.Item>
          {({ active }) => (
            <LinkWithSlug
              to={action.to}
              search={action.search}
              hash={action.hash}
              onClick={clickHandler}
              className={getClassName(active)}
            >
              {action.label}
            </LinkWithSlug>
          )}
        </Menu.Item>
      );
      items.push(node);
    } else {
      const node = (
        <Menu.Item>
          {({ active }) => (
            <button onClick={clickHandler} className={getClassName(active)}>
              {action.label}
            </button>
          )}
        </Menu.Item>
      );
      items.push(node);
    }
  });

  return (
    <>
      <NotificationItem
        {...itemProps}
        role="listitem"
        onClick={() => {
          disclosure.onOpen();
        }}
        secondaryAction={
          items.length > 0 && (
            <TableMenu
              button={
                <MenuButton
                  icon={<EllipsisVerticalIcon className="size-4" />}
                />
              }
            >
              <div className="py-1">
                {items.map((x, idx) => (
                  <Fragment key={idx}>{x}</Fragment>
                ))}
              </div>
              {props.notification.isDismissable && (
                <IfRole action="resolveNotification">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(props.notification);
                          }}
                          className={cn(
                            "group flex w-full items-center px-4 py-2 text-sm",
                            {
                              "bg-gray-100 dark:bg-gray-700": active,
                              "dark:text-white": !active,
                            },
                          )}
                        >
                          Remove alert
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </IfRole>
              )}
            </TableMenu>
          )
        }
      />
      <NotificationDetailModalLazy
        {...disclosure}
        notification={props.notification}
        onRemoveAlert={
          canResolve
            ? () => {
                removeNotification(props.notification);
                disclosure.onClose();
              }
            : undefined
        }
        onActionClick={(options) => {
          disclosure.onClose();
          if (options?.isInternalNavigation) {
            hideSidebar();
          }
        }}
      />
    </>
  );
}
